import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Search, Grid, List } from 'lucide-react';
import NotesList from './NotesList';
import CreateNoteModal from './CreateNoteModal';

const NotesManager = ({ notes, onAddNote, onUpdateNote, onDeleteNote }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [showCreateModal, setShowCreateModal] = useState(false);

  const handleCreateNote = () => {
    console.log('Create note button clicked, opening modal...');
    setShowCreateModal(true);
  };

  // Filter notes based on search
  const filteredNotes = notes.filter(note => 
    (note.name && note.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (note.content && note.content.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100 }
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="h-full"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Project Notes
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Document ideas, meeting notes, and important information
            </p>
          </div>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleCreateNote}
            className="px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>New Note</span>
          </motion.button>
        </div>

        {/* Search and View Controls */}
        <div className="flex flex-col sm:flex-row gap-4 items-center">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search notes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
            />
          </div>

          {/* View Mode Toggle */}
          <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md transition-colors duration-200 ${
                viewMode === 'grid'
                  ? 'bg-white dark:bg-gray-600 text-green-600 dark:text-green-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
              }`}
            >
              <Grid className="h-4 w-4" />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md transition-colors duration-200 ${
                viewMode === 'list'
                  ? 'bg-white dark:bg-gray-600 text-green-600 dark:text-green-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
              }`}
            >
              <List className="h-4 w-4" />
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* Notes List */}
      <motion.div variants={itemVariants}>
        <NotesList 
          notes={filteredNotes} 
          onUpdateNote={onUpdateNote}
          onDeleteNote={onDeleteNote}
          onCreateNote={handleCreateNote}
          viewMode={viewMode}
        />
      </motion.div>

      {/* Stats */}
      <motion.div variants={itemVariants} className="mt-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {notes.length}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Total Notes</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {filteredNotes.length}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Showing</div>
              </div>
            </div>
            
            <div className="text-right">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Last updated: {
                  notes.length > 0 
                    ? new Date(Math.max(...notes.map(n => new Date(n.updatedAt || n.createdAt)))).toLocaleDateString()
                    : 'Never'
                }
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Create Note Modal */}
      <CreateNoteModal 
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onAddNote={onAddNote}
      />
    </motion.div>
  );
};

export default NotesManager;
